import type { MicrosoftSSOWebclientRefreshResDTO } from '@conversed/shared/src/central/dto/microsoft-sso.dto';
import { postRequest } from '@services/httpService';

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  const parts = token.split('.');

  if (parts.length !== 3) {
    return null;
  }

  try {
    // JWT uses URL-safe base64 (- → +, _ → /); pad to a multiple of 4 for atob
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '=',
    );
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
};

// * Malformed tokens are treated as expired so the refresh path is always
// * triggered rather than silently skipping it.
export const isJwtExpired = (token: string, bufferSeconds = 60): boolean => {
  const payload = decodeJwtPayload(token);

  if (!payload || typeof payload.exp !== 'number') {
    return true;
  }

  return Date.now() / 1000 >= payload.exp - bufferSeconds;
};

export const refreshSSOTokens = async (
  refreshToken: string,
): Promise<MicrosoftSSOWebclientRefreshResDTO | null> => {
  try {
    const response = await postRequest<MicrosoftSSOWebclientRefreshResDTO>(
      '/auth/webclient/refresh-token',
      { refreshToken },
    );

    // * Validate the response shape - on auth failure the backend returns an error
    // * body (e.g. { error: 'invalid_grant' }) which is truthy but not a valid DTO.
    if (
      !response?.idToken ||
      !response?.accessToken ||
      !response?.refreshToken
    ) {
      return null;
    }

    return response;
  } catch {
    // * Network failures and invalid responses both fall through to null so the caller shows the re-login screen.
    return null;
  }
};
